<script setup lang="ts">
import { RouterLink, useRoute } from 'vue-router';
import { computed } from 'vue';

const route = useRoute();

interface NavChild {
  to: string;
  label: string;
}
interface NavItem {
  to: string;
  label: string;
  icon: string;
  children?: NavChild[];
}

const navTop: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: '\u25A4' },
  { to: '/players', label: 'Players', icon: '\u25A0' },
  { to: '/areas', label: 'Areas', icon: '\u25A8' },
  { to: '/playlists', label: 'Playlists', icon: '\u266B' },
  {
    to: '/assets',
    label: 'Assets',
    icon: '\u25A6',
    children: [
      { to: '/assets', label: 'Assets' },
      { to: '/assets/builder', label: 'Asset builder' },
    ],
  },
];

const navBottom = [
  { to: '/labels', label: 'Labels', icon: '\u2691' },
  { to: '/licenses', label: 'Licenses', icon: '\u2692' },
  { to: '/settings', label: 'Settings', icon: '\u2699' },
] as const;

const activeName = computed(() => route.name?.toString() ?? '');
</script>

<template>
  <div class="grid grid-cols-[16rem_1fr] min-h-screen">
    <aside
      class="border-r border-slate-800 bg-slate-950/80 p-4 flex flex-col sticky top-0 max-h-screen overflow-y-auto"
    >
      <div class="text-emerald-400 font-bold text-lg mb-8 px-2">piCast</div>
      <nav class="flex flex-col gap-1">
        <template v-for="item in navTop" :key="item.to">
          <RouterLink
            :to="item.to"
            class="flex items-center gap-3 px-3 py-2 rounded-md text-sm hover:bg-slate-800/60"
            :class="
              activeName.startsWith(item.label.toLowerCase().replace(/s$/, ''))
                ? 'bg-slate-800 text-emerald-300'
                : 'text-slate-300'
            "
          >
            <span class="w-4 text-center text-slate-500">{{ item.icon }}</span>
            {{ item.label }}
          </RouterLink>
          <nav
            v-if="item.children && route.path.startsWith(item.to)"
            class="ml-7 flex flex-col gap-1 border-l border-slate-800 pl-2"
          >
            <RouterLink
              v-for="child in item.children"
              :key="child.to"
              :to="child.to"
              class="px-3 py-1.5 rounded-md text-sm hover:bg-slate-800/60"
              :class="route.path === child.to ? 'text-emerald-300' : 'text-slate-400'"
            >
              {{ child.label }}
            </RouterLink>
          </nav>
        </template>
      </nav>
      <nav class="mt-auto flex flex-col gap-1 pt-4 border-t border-slate-800">
        <RouterLink
          v-for="item in navBottom"
          :key="item.to"
          :to="item.to"
          class="flex items-center gap-3 px-3 py-2 rounded-md text-sm hover:bg-slate-800/60"
          :class="
            activeName.startsWith(item.label.toLowerCase().replace(/s$/, ''))
              ? 'bg-slate-800 text-emerald-300'
              : 'text-slate-300'
          "
        >
          <span class="w-4 text-center text-slate-500">{{ item.icon }}</span>
          {{ item.label }}
        </RouterLink>
      </nav>
      <div class="text-xs text-slate-500 px-3 pt-4 mt-4 border-t border-slate-800">v1.0.0</div>
    </aside>

    <main class="p-8 overflow-y-auto">
      <slot />
    </main>
  </div>
</template>
